import POKEMON_ADDITIONS_RAW from '../../../shared/pokemon-additions.json'
import { getFeaturedGmaxRaidSlug, isFeaturedGmaxRaidSlug } from '../../../shared/gmax_featured'
import type { PokemonType } from '~/data/types'

type PokemonAddition = {
  slug: string
  baseSlug: string
  formKind: string
  nameFr: string
  nameEn: string
  generation: number
  types: PokemonType[]
  rarity: string
  spriteSlug: string
  notes?: string
}

type PokemonAdditionsConfig = {
  pokemon: Record<string, PokemonAddition>
}

export type GmaxRaidDifficulty = 'easy' | 'medium' | 'hard' | 'extreme'

export interface GmaxRaidDefinition {
  id: string
  slug: string
  baseSlug: string
  nameFr: string
  nameEn: string
  generation: number
  types: PokemonType[]
  spriteSlug: string
  notes?: string
  difficulty: GmaxRaidDifficulty
  requiredDps: number
  costParticles: number
  fragmentsOnWin: number
  fragmentsOnLoss: number
  guaranteedFragments: number
  pityRequired: number
  baseCatchRate: number
  maxCatchRate: number
}

import {
  GMAX_PARTICLE_CAP,
  GMAX_DAILY_PARTICLES,
  GMAX_STARTING_PARTICLES,
  GMAX_RAID_COST,
  GMAX_GUARANTEED_FRAGMENTS,
  GMAX_PITY_REQUIRED,
} from '../../../shared/gmax_constants'

export {
  GMAX_PARTICLE_CAP,
  GMAX_DAILY_PARTICLES,
  GMAX_STARTING_PARTICLES,
  GMAX_RAID_COST,
  GMAX_GUARANTEED_FRAGMENTS,
  GMAX_PITY_REQUIRED,
}

const ADDITIONS = POKEMON_ADDITIONS_RAW as PokemonAdditionsConfig

const SPECIAL_REQUIRED_DPS: Record<string, number> = {
  'charizard-gmax': 50_000,
  'gengar-gmax': 50_000,
  'lapras-gmax': 50_000,
  'snorlax-gmax': 55_000,
  'melmetal-gmax': 90_000,
  'urshifu-single-gmax': 100_000,
}

function getRequiredDps(entry: PokemonAddition): number {
  const special = SPECIAL_REQUIRED_DPS[entry.slug]
  if (special) return special
  if (entry.generation <= 1) return 32_000
  if (entry.generation <= 5) return 48_000
  if (entry.generation <= 7) return 60_000
  return 72_000
}

function getDifficulty(requiredDps: number): GmaxRaidDifficulty {
  if (requiredDps >= 90_000) return 'extreme'
  if (requiredDps >= 65_000) return 'hard'
  if (requiredDps >= 45_000) return 'medium'
  return 'easy'
}

function getBaseCatchRate(difficulty: GmaxRaidDifficulty): number {
  switch (difficulty) {
    case 'easy':
      return 0.12
    case 'medium':
      return 0.09
    case 'hard':
      return 0.07
    case 'extreme':
      return 0.05
  }
}

function toRaid(entry: PokemonAddition): GmaxRaidDefinition {
  const requiredDps = getRequiredDps(entry)
  const difficulty = getDifficulty(requiredDps)
  return {
    id: `gmax-${entry.slug}`,
    slug: entry.slug,
    baseSlug: entry.baseSlug,
    nameFr: entry.nameFr,
    nameEn: entry.nameEn,
    generation: entry.generation,
    types: entry.types,
    spriteSlug: entry.spriteSlug,
    notes: entry.notes,
    difficulty,
    requiredDps,
    costParticles: GMAX_RAID_COST,
    fragmentsOnWin: 8,
    fragmentsOnLoss: 3,
    guaranteedFragments: GMAX_GUARANTEED_FRAGMENTS,
    pityRequired: GMAX_PITY_REQUIRED,
    baseCatchRate: getBaseCatchRate(difficulty),
    maxCatchRate: difficulty === 'extreme' ? 0.24 : 0.32,
  }
}

export const GMAX_RAIDS: GmaxRaidDefinition[] = Object.values(ADDITIONS.pokemon)
  .filter((entry) => entry.formKind === 'gmax')
  .map(toRaid)
  .sort((a, b) => a.generation - b.generation || a.requiredDps - b.requiredDps || a.nameFr.localeCompare(b.nameFr))

export const GMAX_RAID_BY_SLUG = new Map(GMAX_RAIDS.map((raid) => [raid.slug, raid]))

/** Slugs de base des espèces dont la forme Gigamax se débloque via les antres. */
export const GMAX_RAID_BASE_SLUGS: ReadonlySet<string> = new Set(GMAX_RAIDS.map((raid) => raid.baseSlug))

const GMAX_RAID_SLUGS = GMAX_RAIDS.map((raid) => raid.slug)

/** @deprecated Préférer `getGmaxFeaturedRaidSlug()` (rotation hebdomadaire UTC). */
export const GMAX_FEATURED_RAID_SLUG = GMAX_RAIDS[0]?.slug ?? ''

export function getGmaxFeaturedRaidSlug(date: Date = new Date()): string {
  return getFeaturedGmaxRaidSlug(GMAX_RAID_SLUGS, date)
}

export function isFeaturedGmaxRaid(slug: string, date: Date = new Date()): boolean {
  return isFeaturedGmaxRaidSlug(slug, GMAX_RAID_SLUGS, date)
}

export function getGmaxRaid(slug: string): GmaxRaidDefinition | undefined {
  return GMAX_RAID_BY_SLUG.get(slug)
}

export { estimateGmaxCatchRate } from '../../../shared/gmax_raid_logic'
